import React, { useMemo, useState } from 'react';
import { CompleteKundali, Language, PlanetName, PlanetPosition } from '../../types/kundali';
import { 
  calculateAllPlanetaryTransitPeriods, 
  PlanetaryTransitPeriod 
} from '../../utils/gocharPeriodEngine';
import { 
  calculateLivePlanetaryEphemeris, 
  calculateDoubleTransit 
} from '../../utils/vedicEngine';
import { calculateHouseDistance } from '../../utils/gocharVedhaEngine';
import { ZODIAC_HI } from '../../utils/language';
import { formatDate } from '../../utils/dateUtils';
import {
  Calendar,
  Clock,
  ArrowRight,
  Sparkles,
  Layers,
  FileCheck2,
  ShieldCheck,
  ChevronRight,
  Info,
  Filter,
  Eye,
  RotateCcw,
  Zap,
  TrendingUp,
  MapPin,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';

export interface UnifiedGocharTimelineProps {
  kundali: CompleteKundali;
  targetAnalysisDateTime: Date;
  onTargetAnalysisDateTimeChange?: (date: Date) => void;
  selectedTransitPlanet?: PlanetName | null;
  onSelectTransitPlanet?: (planet: PlanetName) => void;
  selectedPerspective?: 'lagna' | 'moon' | 'sun';
  onSelectPerspective?: (perspective: 'lagna' | 'moon' | 'sun') => void;
  onOpenEvidenceDrawer?: (planet?: PlanetName) => void;
  onInspectMatrix?: (planet: PlanetName, house: number) => void;
  onInspectAshtakavarga?: (planet: PlanetName, house: number) => void;
  lang?: Language;
}

export type TimelineFilter = 'ALL' | 'MAJOR' | 'INNER' | PlanetName;

export interface TimelineEventItem {
  id: string;
  planet: PlanetName;
  type: 'INGRESS' | 'CURRENT_TRANSIT' | 'UPCOMING_INGRESS' | 'DOUBLE_TRANSIT';
  eventDate: Date;
  eventDateFormatted: string;
  temporalPhase: 'PAST' | 'CURRENT' | 'UPCOMING';
  sign: string;
  signHi: string;
  signIndex: number;
  houseFromLagna: number;
  houseFromMoon: number;
  houseFromSun: number;
  houseInPerspective: number;
  isRetrograde: boolean;
  progressPercentage?: number;
  daysRemaining?: number;
  daysElapsed?: number;
  durationFormatted?: string;
  degreeFormatted?: string;
  titleEn: string;
  titleHi: string;
  descriptionEn: string;
  descriptionHi: string;
  natalContacts: { natalPlanet: string; natureEn: string; natureHi: string; orb: number; house: number }[];
  doubleTransitHouses?: number[];
  bavPoints?: number;
  savPoints?: number;
}

const PLANET_ORDER: PlanetName[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'];
const SLOW_PLANETS: PlanetName[] = ['Jupiter', 'Saturn', 'Rahu', 'Ketu', 'Mars'];
const INNER_PLANETS: PlanetName[] = ['Sun', 'Moon', 'Mercury', 'Venus'];

export const UnifiedGocharTimeline: React.FC<UnifiedGocharTimelineProps> = ({
  kundali,
  targetAnalysisDateTime,
  onTargetAnalysisDateTimeChange,
  selectedTransitPlanet,
  onSelectTransitPlanet,
  selectedPerspective = 'lagna',
  onSelectPerspective,
  onOpenEvidenceDrawer,
  onInspectMatrix,
  onInspectAshtakavarga,
  lang = 'en'
}) => {
  const isHi = lang === 'hi';
  const [activeFilter, setActiveFilter] = useState<TimelineFilter>('ALL');
  const [activeTab, setActiveTab] = useState<'ALL_EVENTS' | 'CURRENT_PHASE' | 'UPCOMING_INGRESSES'>('ALL_EVENTS');

  // Natal Reference Sign Indices
  const lagnaHouse = kundali.houses[0];
  const lagnaSignIndex = lagnaHouse ? lagnaHouse.signIndex : 0;
  const moonPlanet = kundali.planets.find(p => p.name === 'Moon');
  const moonSignIndex = moonPlanet ? moonPlanet.signIndex : 0;
  const sunPlanet = kundali.planets.find(p => p.name === 'Sun');
  const sunSignIndex = sunPlanet ? sunPlanet.signIndex : 0;

  const referenceSignIndex = useMemo(() => {
    if (selectedPerspective === 'moon') return moonSignIndex;
    if (selectedPerspective === 'sun') return sunSignIndex;
    return lagnaSignIndex;
  }, [selectedPerspective, moonSignIndex, sunSignIndex, lagnaSignIndex]);

  // Authoritative Calculation Core: Ephemeris, Periods, Double Transit
  const liveEphemeris = useMemo(() => {
    return calculateLivePlanetaryEphemeris(targetAnalysisDateTime);
  }, [targetAnalysisDateTime]);

  const allTransitPeriods = useMemo(() => {
    return calculateAllPlanetaryTransitPeriods(targetAnalysisDateTime);
  }, [targetAnalysisDateTime]);

  const doubleTransitInfo = useMemo(() => {
    const activeHouses = calculateDoubleTransit(kundali.planets, targetAnalysisDateTime);
    return { activeHouses };
  }, [kundali.planets, targetAnalysisDateTime]);

  // Extract SAV & BAV from authoritative kundali
  const sarvashtakavarga = kundali.ashtakvarga?.sarvashtakvarga || [];
  const bhinna = kundali.ashtakvarga?.bhinna || {};

  // Build Chronological Events
  const { currentEvents, pastEvents, upcomingEvents, allTimelineEvents } = useMemo(() => {
    const currents: TimelineEventItem[] = [];
    const pasts: TimelineEventItem[] = [];
    const upcomings: TimelineEventItem[] = [];

    PLANET_ORDER.forEach(planet => {
      const period = allTransitPeriods[planet];
      const ephemPos = liveEphemeris.find(p => p.planet === planet);
      if (!period || !ephemPos) return;

      const houseFromLagna = calculateHouseDistance(lagnaSignIndex, period.signIndex);
      const houseFromMoon = calculateHouseDistance(moonSignIndex, period.signIndex);
      const houseFromSun = calculateHouseDistance(sunSignIndex, period.signIndex);
      const houseInPerspective = calculateHouseDistance(referenceSignIndex, period.signIndex);

      const signHi = ZODIAC_HI[period.sign] || period.sign;
      const nextSignHi = ZODIAC_HI[period.nextSign] || period.nextSign;

      // SAV & BAV points for current placement
      const savPoints = sarvashtakavarga[houseFromLagna - 1] ?? 28;
      const bavPlanetScores = bhinna[planet as keyof typeof bhinna] || [];
      const bavPoints = bavPlanetScores[houseFromLagna - 1] ?? 4;

      // Natal Parashari Contacts
      const natalContacts: { natalPlanet: string; natureEn: string; natureHi: string; orb: number; house: number }[] = [];
      (kundali.planets || []).forEach(np => {
        if (np.name !== 'Ascendant') {
          const dist = calculateHouseDistance(np.signIndex, period.signIndex);
          const orb = Math.abs(ephemPos.degreeInSign - (np.degreeInSign || 0));
          if (dist === 1) {
            natalContacts.push({
              natalPlanet: np.name,
              natureEn: 'Conjunction (Yuti)',
              natureHi: 'युति',
              orb: Number(orb.toFixed(1)),
              house: np.house
            });
          } else if (dist === 7) {
            natalContacts.push({
              natalPlanet: np.name,
              natureEn: '7th Full Aspect',
              natureHi: 'सप्तम पूर्ण दृष्टि',
              orb: Number(orb.toFixed(1)),
              house: np.house
            });
          } else if (planet === 'Mars' && (dist === 4 || dist === 8)) {
            natalContacts.push({
              natalPlanet: np.name,
              natureEn: `Mars ${dist}th Special Aspect`,
              natureHi: `मंगल ${dist}म विशेष दृष्टि`,
              orb: Number(orb.toFixed(1)),
              house: np.house
            });
          } else if ((planet === 'Jupiter' || planet === 'Rahu' || planet === 'Ketu') && (dist === 5 || dist === 9)) {
            natalContacts.push({
              natalPlanet: np.name,
              natureEn: `${planet} ${dist}th Special Aspect`,
              natureHi: `${planet} ${dist}म विशेष दृष्टि`,
              orb: Number(orb.toFixed(1)),
              house: np.house
            });
          } else if (planet === 'Saturn' && (dist === 3 || dist === 10)) {
            natalContacts.push({
              natalPlanet: np.name,
              natureEn: `Saturn ${dist}th Special Aspect`,
              natureHi: `शनि ${dist}म विशेष दृष्टि`,
              orb: Number(orb.toFixed(1)),
              house: np.house
            });
          }
        }
      });

      // 1. CURRENT TRANSIT EVENT
      const currentItem: TimelineEventItem = {
        id: `CURRENT_${planet}_${period.sign}`,
        planet,
        type: 'CURRENT_TRANSIT',
        eventDate: targetAnalysisDateTime,
        eventDateFormatted: formatDate(targetAnalysisDateTime),
        temporalPhase: 'CURRENT',
        sign: period.sign,
        signHi,
        signIndex: period.signIndex,
        houseFromLagna,
        houseFromMoon,
        houseFromSun,
        houseInPerspective,
        isRetrograde: period.isRetrograde,
        progressPercentage: period.progressPercentage,
        daysRemaining: period.daysRemaining,
        daysElapsed: period.daysElapsed,
        durationFormatted: period.durationFormatted,
        degreeFormatted: ephemPos.formattedDegree,
        bavPoints,
        savPoints,
        titleEn: `${planet} in ${period.sign} (H${houseInPerspective})`,
        titleHi: `${planet} ${signHi} राशि में (भाव ${houseInPerspective})`,
        descriptionEn: `Active transit period (${period.startDateFormatted} → ${period.endDateFormatted}) • ${period.progressPercentage}% of duration elapsed (${period.daysRemaining} days remaining).`,
        descriptionHi: `सक्रिय गोचर काल (${period.startDateFormatted} → ${period.endDateFormatted}) • अवधि का ${period.progressPercentage}% पूर्ण (${period.daysRemaining} दिन शेष)।`,
        natalContacts
      };
      currents.push(currentItem);

      // 2. PAST INGRESS EVENT
      const pastItem: TimelineEventItem = {
        id: `PAST_INGRESS_${planet}_${period.sign}`,
        planet,
        type: 'INGRESS',
        eventDate: period.startDate,
        eventDateFormatted: period.startDateFormatted,
        temporalPhase: 'PAST',
        sign: period.sign,
        signHi,
        signIndex: period.signIndex,
        houseFromLagna,
        houseFromMoon,
        houseFromSun,
        houseInPerspective,
        isRetrograde: period.isRetrograde,
        daysElapsed: period.daysElapsed,
        durationFormatted: period.durationFormatted,
        bavPoints,
        savPoints,
        titleEn: `${planet} entered ${period.sign}`,
        titleHi: `${planet} का ${signHi} राशि में प्रवेश`,
        descriptionEn: `Ingress into ${period.sign} occurred on ${period.startDateFormatted} (${period.daysElapsed} days ago).`,
        descriptionHi: `${signHi} राशि में प्रवेश ${period.startDateFormatted} को हुआ था (${period.daysElapsed} दिन पूर्व)।`,
        natalContacts
      };
      pasts.push(pastItem);

      // 3. UPCOMING INGRESS EVENT
      const upcomingHouseFromLagna = calculateHouseDistance(lagnaSignIndex, period.nextSignIndex);
      const upcomingHouseInPerspective = calculateHouseDistance(referenceSignIndex, period.nextSignIndex);
      const upcomingSav = sarvashtakavarga[upcomingHouseFromLagna - 1] ?? 28;
      const upcomingBav = (bhinna[planet as keyof typeof bhinna] || [])[upcomingHouseFromLagna - 1] ?? 4;

      const upcomingItem: TimelineEventItem = {
        id: `UPCOMING_INGRESS_${planet}_${period.nextSign}`,
        planet,
        type: 'UPCOMING_INGRESS',
        eventDate: period.nextIngressDate,
        eventDateFormatted: period.nextIngressDateFormatted,
        temporalPhase: 'UPCOMING',
        sign: period.nextSign,
        signHi: nextSignHi,
        signIndex: period.nextSignIndex,
        houseFromLagna: upcomingHouseFromLagna,
        houseFromMoon: calculateHouseDistance(moonSignIndex, period.nextSignIndex),
        houseFromSun: calculateHouseDistance(sunSignIndex, period.nextSignIndex),
        houseInPerspective: upcomingHouseInPerspective,
        isRetrograde: false,
        daysRemaining: period.daysUntilNextIngress,
        durationFormatted: period.nextDurationFormatted,
        bavPoints: upcomingBav,
        savPoints: upcomingSav,
        titleEn: `${planet} enters ${period.nextSign} (H${upcomingHouseInPerspective})`,
        titleHi: `${planet} का ${nextSignHi} राशि में आगामी प्रवेश (भाव ${upcomingHouseInPerspective})`,
        descriptionEn: `Next sign transit commences ${period.nextIngressDateFormatted} (in ${period.daysUntilNextIngress} days) until ${period.nextPeriodEndDateFormatted} (~${period.nextDurationFormatted}).`,
        descriptionHi: `अगली राशि का गोचर ${period.nextIngressDateFormatted} को (${period.daysUntilNextIngress} दिनों में) प्रारंभ होगा और ${period.nextPeriodEndDateFormatted} तक रहेगा (~${period.nextDurationFormatted})।`,
        natalContacts: []
      };
      upcomings.push(upcomingItem);
    });

    // Sort past events (most recent first)
    pasts.sort((a, b) => b.eventDate.getTime() - a.eventDate.getTime());

    // Sort upcoming events (nearest future first)
    upcomings.sort((a, b) => a.eventDate.getTime() - b.eventDate.getTime());

    // Unified Chronological Stream: Past (descending to moment) -> Current -> Upcoming
    const all = [...pasts, ...currents, ...upcomings];

    return {
      currentEvents: currents,
      pastEvents: pasts,
      upcomingEvents: upcomings,
      allTimelineEvents: all
    };
  }, [allTransitPeriods, liveEphemeris, targetAnalysisDateTime, lagnaSignIndex, moonSignIndex, sunSignIndex, referenceSignIndex, kundali.planets, sarvashtakavarga, bhinna]);

  // Filtered Events
  const filteredEvents = useMemo(() => {
    let baseList = allTimelineEvents;
    if (activeTab === 'CURRENT_PHASE') {
      baseList = currentEvents;
    } else if (activeTab === 'UPCOMING_INGRESSES') {
      baseList = upcomingEvents;
    }

    if (activeFilter === 'ALL') {
      return baseList;
    }
    if (activeFilter === 'MAJOR') {
      return baseList.filter(e => SLOW_PLANETS.includes(e.planet));
    }
    if (activeFilter === 'INNER') {
      return baseList.filter(e => INNER_PLANETS.includes(e.planet));
    }
    return baseList.filter(e => e.planet === activeFilter);
  }, [allTimelineEvents, currentEvents, upcomingEvents, activeTab, activeFilter]);

  const handleJumpToNow = () => {
    if (onTargetAnalysisDateTimeChange) {
      onTargetAnalysisDateTimeChange(new Date());
    }
  };

  const handleJumpToEvent = (date: Date, planet: PlanetName) => {
    if (onSelectTransitPlanet) {
      onSelectTransitPlanet(planet);
    }
    if (onTargetAnalysisDateTimeChange) {
      onTargetAnalysisDateTimeChange(date);
    }
  };

  return (
    <div className="bg-[#FFFDF7] border border-[#B58A4A]/30 rounded-2xl p-5 shadow-xs space-y-6">
      {/* 1. Header & Navigation Controls */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-stone-200 pb-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#174C4B]" />
            <h3 className="font-bold text-[#174C4B] font-serif text-lg">
              {isHi ? 'एकीकृत गोचर कालानुक्रम समय-रेखा (Unified Gochar Timeline)' : 'Unified Gochar Temporal Timeline'}
            </h3>
            <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-[#174C4B]/10 text-[#174C4B] border border-[#174C4B]/20">
              P2-B Core
            </span>
          </div>
          <p className="text-xs text-stone-600">
            {isHi
              ? 'वर्तमान विश्लेषण क्षण के सापेक्ष वास्तविक खगोलीय राशि प्रवेश, सक्रिय गोचर एवं आगामी कालखण्ड'
              : 'Chronological transit milestones, active sign periods, and upcoming zodiac ingresses relative to analysis moment.'}
          </p>
        </div>

        {/* Action Controls: Jump to Current & Perspective */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Tri-Lagna Perspective Selector */}
          <div className="flex items-center rounded-lg border border-[#B58A4A]/40 bg-white p-0.5 text-xs">
            <button
              onClick={() => onSelectPerspective && onSelectPerspective('lagna')}
              className={`px-2.5 py-1 rounded-md font-semibold transition-all ${
                selectedPerspective === 'lagna' ? 'bg-[#174C4B] text-white' : 'text-stone-700 hover:text-[#174C4B]'
              }`}
            >
              {isHi ? 'लग्न' : 'Janma Lagna'}
            </button>
            <button
              onClick={() => onSelectPerspective && onSelectPerspective('moon')}
              className={`px-2.5 py-1 rounded-md font-semibold transition-all ${
                selectedPerspective === 'moon' ? 'bg-[#174C4B] text-white' : 'text-stone-700 hover:text-[#174C4B]'
              }`}
            >
              {isHi ? 'चंद्र' : 'Chandra Lagna'}
            </button>
            <button
              onClick={() => onSelectPerspective && onSelectPerspective('sun')}
              className={`px-2.5 py-1 rounded-md font-semibold transition-all ${
                selectedPerspective === 'sun' ? 'bg-[#174C4B] text-white' : 'text-stone-700 hover:text-[#174C4B]'
              }`}
            >
              {isHi ? 'सूर्य' : 'Surya Lagna'}
            </button>
          </div>

          <button
            onClick={handleJumpToNow}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#174C4B] hover:bg-[#174C4B]/90 text-white text-xs font-bold transition-all shadow-2xs"
            title={isHi ? 'वर्तमान समय पर रीसेट करें' : 'Reset to Live Now'}
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span>{isHi ? 'वर्तमान समय (Now)' : 'Jump to Now'}</span>
          </button>
        </div>
      </div>

      {/* 2. Analysis Moment Status Bar & Double Transit Banner */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="p-3.5 rounded-xl bg-white border border-[#B58A4A]/25 space-y-1">
          <div className="text-[10px] uppercase font-bold tracking-wider text-stone-500 flex items-center gap-1">
            <Clock className="w-3 h-3 text-[#B58A4A]" />
            <span>{isHi ? 'लक्ष्य विश्लेषण समय' : 'Analysis Anchor Moment'}</span>
          </div>
          <div className="font-mono text-sm font-bold text-[#174C4B]">
            {formatDate(targetAnalysisDateTime)} • {targetAnalysisDateTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
          <div className="text-[11px] text-stone-500">
            {isHi ? 'सभी ग्रह स्थितियां व दृष्टि संबंध इसी क्षण पर आधारित हैं' : 'All transit ingresses & contacts anchored to this instant.'}
          </div>
        </div>

        {/* Live Double Transit Banner */}
        <div className="md:col-span-2 p-3.5 rounded-xl bg-[#174C4B]/5 border border-[#174C4B]/20 flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-xs font-bold text-[#174C4B]">
              <Sparkles className="w-4 h-4 text-[#B58A4A]" />
              <span>{isHi ? 'गुरु-शनि संयुक्त गोचर (Double Transit Context)' : 'Jupiter–Saturn Double Transit Activation'}</span>
            </div>
            <span className="text-[10px] font-mono font-bold bg-[#174C4B]/10 text-[#174C4B] px-2 py-0.5 rounded">
              {doubleTransitInfo?.activeHouses?.length || 0} {isHi ? 'भाव सक्रिय' : 'Houses Energized'}
            </span>
          </div>
          <p className="text-xs text-stone-700 mt-1 leading-relaxed">
            {doubleTransitInfo?.activeHouses?.length ? (
              isHi 
                ? `गुरु और शनि का संयुक्त प्रभाव जन्म कुंडली के भाव ${doubleTransitInfo.activeHouses.join(', ')} पर पड़ रहा है।`
                : `Simultaneous activation on Houses ${doubleTransitInfo.activeHouses.join(', ')} creates manifest potential under Parashari rules.`
            ) : (
              isHi ? 'वर्तमान विश्लेषण क्षण पर कोई संयुक्त भाव प्रभाव नहीं है।' : 'No dual-influence house overlap active at this exact moment.'
            )}
          </p>
        </div>
      </div>

      {/* 3. Filter Navigation Strip */}
      <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
        {/* Tab Selection */}
        <div className="flex items-center gap-1 bg-stone-100 dark:bg-stone-800 p-1 rounded-xl text-xs font-medium">
          <button
            onClick={() => setActiveTab('ALL_EVENTS')}
            className={`px-3 py-1.5 rounded-lg transition-all ${
              activeTab === 'ALL_EVENTS' ? 'bg-white shadow-2xs text-[#174C4B] font-bold' : 'text-stone-600 hover:text-stone-900'
            }`}
          >
            {isHi ? 'सम्पूर्ण कालानुक्रम' : 'Chronological Stream'}
          </button>
          <button
            onClick={() => setActiveTab('CURRENT_PHASE')}
            className={`px-3 py-1.5 rounded-lg transition-all ${
              activeTab === 'CURRENT_PHASE' ? 'bg-white shadow-2xs text-[#174C4B] font-bold' : 'text-stone-600 hover:text-stone-900'
            }`}
          >
            {isHi ? 'सक्रिय गोचर (Now)' : 'Active Transits (9)'}
          </button>
          <button
            onClick={() => setActiveTab('UPCOMING_INGRESSES')}
            className={`px-3 py-1.5 rounded-lg transition-all ${
              activeTab === 'UPCOMING_INGRESSES' ? 'bg-white shadow-2xs text-[#174C4B] font-bold' : 'text-stone-600 hover:text-stone-900'
            }`}
          >
            {isHi ? 'आगामी राशि प्रवेश' : 'Upcoming Ingresses'}
          </button>
        </div>

        {/* Planet Filter Pills */}
        <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
          <span className="text-stone-500 font-semibold mr-1 flex items-center gap-1">
            <Filter className="w-3 h-3 text-[#B58A4A]" />
            {isHi ? 'फिल्टर:' : 'Filter:'}
          </span>
          <button
            onClick={() => setActiveFilter('ALL')}
            className={`px-2.5 py-1 rounded-lg border font-semibold transition-all ${
              activeFilter === 'ALL' ? 'bg-[#174C4B] text-white border-[#174C4B]' : 'bg-white text-stone-700 border-stone-200 hover:bg-stone-50'
            }`}
          >
            {isHi ? 'सभी ग्रह' : 'All Grahas'}
          </button>
          <button
            onClick={() => setActiveFilter('MAJOR')}
            className={`px-2.5 py-1 rounded-lg border font-semibold transition-all ${
              activeFilter === 'MAJOR' ? 'bg-[#174C4B] text-white border-[#174C4B]' : 'bg-white text-stone-700 border-stone-200 hover:bg-stone-50'
            }`}
          >
            {isHi ? 'धीमी गति (गुरु/शनि/राहु/केतु/मंगल)' : 'Slow Grahas'}
          </button>
          <button
            onClick={() => setActiveFilter('INNER')}
            className={`px-2.5 py-1 rounded-lg border font-semibold transition-all ${
              activeFilter === 'INNER' ? 'bg-[#174C4B] text-white border-[#174C4B]' : 'bg-white text-stone-700 border-stone-200 hover:bg-stone-50'
            }`}
          >
            {isHi ? 'तीव्र गति (सूर्य/चंद्र/बुध/शुक्र)' : 'Fast Grahas'}
          </button>
        </div>
      </div>

      {/* 4. Timeline Event Cards List */}
      <div className="space-y-3.5">
        {filteredEvents.length === 0 ? (
          <div className="text-center py-12 text-xs text-stone-500 bg-white rounded-xl border border-stone-200">
            {isHi ? 'चुने गए फ़िल्टर के लिए कोई घटना उपलब्ध नहीं है।' : 'No timeline events match the selected filter.'}
          </div>
        ) : (
          filteredEvents.map((evt) => {
            const isSelected = selectedTransitPlanet === evt.planet;
            const isPast = evt.temporalPhase === 'PAST';
            const isCurrent = evt.temporalPhase === 'CURRENT';
            const isUpcoming = evt.temporalPhase === 'UPCOMING';

            let phaseBadgeClass = 'bg-stone-100 text-stone-700 border-stone-300';
            let phaseLabel = isHi ? 'अतीत प्रवेश' : 'Past Ingress';
            if (isCurrent) {
              phaseBadgeClass = 'bg-emerald-100 text-emerald-900 border-emerald-300 font-bold';
              phaseLabel = isHi ? 'सक्रिय गोचर (Now)' : 'Active Transit';
            } else if (isUpcoming) {
              phaseBadgeClass = 'bg-purple-100 text-purple-900 border-purple-300 font-bold';
              phaseLabel = isHi ? 'आगामी प्रवेश' : 'Upcoming Ingress';
            }

            return (
              <div
                key={evt.id}
                onClick={() => onSelectTransitPlanet && onSelectTransitPlanet(evt.planet)}
                className={`p-4 rounded-xl border transition-all cursor-pointer bg-white ${
                  isSelected 
                    ? 'border-[#B58A4A] ring-2 ring-[#B58A4A]/25 shadow-sm' 
                    : 'border-stone-200 hover:border-[#174C4B]/40 hover:shadow-2xs'
                }`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  {/* Planet & Event Header */}
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-base font-serif border ${
                      isSelected 
                        ? 'bg-[#174C4B] text-white border-[#174C4B]' 
                        : 'bg-[#174C4B]/10 text-[#174C4B] border-[#174C4B]/20'
                    }`}>
                      {evt.planet.slice(0, 2)}
                    </div>

                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-bold text-[#174C4B] text-sm">
                          {isHi ? evt.titleHi : evt.titleEn}
                        </h4>
                        <span className={`text-[10px] px-2 py-0.5 rounded border ${phaseBadgeClass}`}>
                          {phaseLabel}
                        </span>
                        {evt.isRetrograde && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-300">
                            ℞ Retrograde
                          </span>
                        )}
                        {isSelected && (
                          <span className="text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-[#B58A4A]/20 text-[#174C4B] border border-[#B58A4A]/40">
                            Selected
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-3 text-xs text-stone-500 mt-0.5 flex-wrap">
                        <span className="flex items-center gap-1 font-mono">
                          <Calendar className="w-3 h-3 text-[#B58A4A]" />
                          {evt.eventDateFormatted}
                        </span>
                        <span>•</span>
                        <span>
                          {selectedPerspective === 'lagna' ? 'Lagna' : selectedPerspective === 'moon' ? 'Chandra' : 'Surya'} H{evt.houseInPerspective} ({evt.sign})
                        </span>
                        {evt.savPoints !== undefined && (
                          <>
                            <span>•</span>
                            <span className="font-mono font-semibold text-stone-700">
                              SAV: <strong>{evt.savPoints}</strong> bindus
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions & Deep Links */}
                  <div className="flex items-center gap-1.5 self-end sm:self-center shrink-0">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleJumpToEvent(evt.eventDate, evt.planet);
                      }}
                      className="px-2.5 py-1.5 rounded-lg bg-stone-100 hover:bg-[#174C4B] hover:text-white text-stone-700 text-xs font-semibold transition-all border border-stone-200 flex items-center gap-1"
                      title={isHi ? 'इस घटना की तिथि पर जाएं' : 'Set analysis moment to this event'}
                    >
                      <Clock className="w-3 h-3" />
                      <span>{isHi ? 'तिथि चुनें' : 'Jump'}</span>
                    </button>

                    {onInspectMatrix && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onInspectMatrix(evt.planet, evt.houseInPerspective);
                        }}
                        className="px-2.5 py-1.5 rounded-lg bg-stone-100 hover:bg-[#174C4B] hover:text-white text-stone-700 text-xs font-semibold transition-all border border-stone-200 flex items-center gap-1"
                        title={isHi ? 'दृष्टि मैट्रिक्स में देखें (P2-A)' : 'Inspect in Gochar Drishti Matrix (P2-A)'}
                      >
                        <Layers className="w-3 h-3" />
                        <span>{isHi ? 'मैट्रिक्स' : 'Matrix'}</span>
                      </button>
                    )}

                    {onInspectAshtakavarga && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onInspectAshtakavarga(evt.planet, evt.houseInPerspective);
                        }}
                        className="px-2.5 py-1.5 rounded-lg bg-stone-100 hover:bg-[#174C4B] hover:text-white text-stone-700 text-xs font-semibold transition-all border border-stone-200 flex items-center gap-1"
                        title={isHi ? 'अष्टकवर्ग विवरण देखें (P2-C)' : 'Inspect BAV/SAV context (P2-C)'}
                      >
                        <Sparkles className="w-3 h-3" />
                        <span>{isHi ? 'BAV/SAV' : 'BAV/SAV'}</span>
                      </button>
                    )}

                    {onOpenEvidenceDrawer && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenEvidenceDrawer(evt.planet);
                        }}
                        className="p-1.5 rounded-lg bg-stone-100 hover:bg-[#174C4B] hover:text-white text-stone-700 transition-all border border-stone-200"
                        title={isHi ? '6-भाग प्रमाण देखें' : 'Inspect 6-Part Epistemic Evidence'}
                      >
                        <FileCheck2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Progress Bar for Current Transits */}
                {isCurrent && evt.progressPercentage !== undefined && (
                  <div className="mt-3 pt-3 border-t border-stone-100 space-y-1.5">
                    <div className="flex items-center justify-between text-[11px] text-stone-600">
                      <span className="font-semibold text-[#174C4B]">
                        {isHi ? 'गोचर कालखंड प्रगति (Transit Duration Progress)' : 'Transit Duration Progress'}
                      </span>
                      <span className="font-mono font-bold text-[#174C4B]">
                        {evt.progressPercentage}% ({evt.daysRemaining} days remaining)
                      </span>
                    </div>
                    <div className="w-full bg-stone-100 rounded-full h-2 overflow-hidden border border-stone-200">
                      <div 
                        className="bg-[#174C4B] h-full rounded-full transition-all duration-300"
                        style={{ width: `${Math.min(100, Math.max(0, evt.progressPercentage))}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Natal Contacts Sub-strip */}
                {evt.natalContacts && evt.natalContacts.length > 0 && (
                  <div className="mt-2.5 pt-2 border-t border-stone-100 flex flex-wrap items-center gap-2 text-xs">
                    <span className="text-[10px] uppercase font-bold text-stone-500">
                      {isHi ? 'जन्म ग्रह संपर्क:' : 'Natal Contacts:'}
                    </span>
                    {evt.natalContacts.map((nc, idx) => (
                      <span 
                        key={idx}
                        className="px-2 py-0.5 rounded bg-[#174C4B]/5 text-[#174C4B] border border-[#174C4B]/20 text-[11px] font-medium flex items-center gap-1"
                      >
                        <Sparkles className="w-3 h-3 text-[#B58A4A]" />
                        <span>{isHi ? nc.natureHi : nc.natureEn} with {nc.natalPlanet} (orb {nc.orb}°)</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* 5. Epistemic Precision & Ephemeris Resolution Notice */}
      <div className="p-3.5 rounded-xl bg-white border border-stone-200 text-xs text-stone-600 space-y-1">
        <div className="flex items-center gap-1.5 font-bold text-[#174C4B]">
          <Info className="w-4 h-4 text-[#B58A4A]" />
          <span>{isHi ? 'खगोलीय शुद्धता एवं गणना परिशुद्धता विवरण' : 'Astronomical Accuracy & Epistemic Precision Notice'}</span>
        </div>
        <p className="text-[11px] leading-relaxed text-stone-500">
          {isHi
            ? 'गोचर प्रवेश एवं निकास कालखंड निरयण लाहिड़ी पंचांग एवं 5-मिनट बाइनरी सर्च रेज़ोल्यूशन पर आधारित हैं। कालखंड प्रगति purely informational/temporal है और इसे किसी निश्चित फलित संभावना के रूप में नहीं देखा जाना चाहिए।'
            : 'All ingress and egress milestones are computed deterministically via the Sidereal Lahiri Ephemeris engine with 5-minute binary-search precision. Transit duration progress is purely temporal/informational and does not imply predictive certainty.'}
        </p>
      </div>
    </div>
  );
};
